import { getCmdbServices } from '@/redux/actions/customerActions';
import { getAssignedMachines } from '@/redux/actions/gpuActions';
import { colorAccent, colorAccentHover } from '@/utils/palette';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import GpuOneDetailView from '../components/GpuOneDetailView';
import GpuOneFilterSection from '../components/GpuOneFilterSection';
import GpuOneTable from '../components/GpuOneTable';
import useGpuOneFilter from '../hooks/useGpuOneFilter';

const GPUOnePage = () => {
	const dispatch = useDispatch();
	const permissions = useSelector(
		state => state?.permissionsStorage?.allModulePermissions?.gpu,
	);
	const { scopePermissions } = useSelector(state => state?.permissionsStorage);
	const { customerID } = useSelector(state => state?.customer?.currentCustomer);
	const { gpuoneLoginNode } = useSelector(
		state => state?.customer?.currentCustomer,
	);
	const siteID = useSelector(state => state?.site?.currentSite?.key);
	const { cmdbServices } = useSelector(state => state?.customer);
	const { customerGPUOne, isLoading, error } = useSelector(
		state => state.gpuOne,
	);

	const [selectedGPU, setSelectedGPU] = useState(null);
	const [viewMode, setViewMode] = useState('table'); // 'table' or 'detail'

	// Use the custom hook for filtering
	const gpuData = customerGPUOne?.machines || [];
	const { filters, filteredGPUData, handleFilterChange, handleClearFilters } =
		useGpuOneFilter(gpuData);

	useEffect(() => {
		dispatch(getCmdbServices());
	}, [customerID, siteID]);

	useEffect(() => {
		if (customerID) {
			dispatch(getAssignedMachines(customerID));
		}
	}, [customerID]);

	const handleGPUClick = gpu => {
		setSelectedGPU(gpu);
		setViewMode('detail');
	};

	const handleBackToTable = () => {
		setViewMode('table');
		setSelectedGPU(null);
	};

	const handleDataRefresh = () => {
		if (customerID) {
			dispatch(getAssignedMachines(customerID));
		}
	};

	if (!scopePermissions?.isStnUser && !cmdbServices?.managedGPUOne) {
		return (
			<Container>
				<div
					style={{
						height: '50vh',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<p>
						You are not currently subscribed to this service. For assistance,
						please contact Support.
					</p>
				</div>
			</Container>
		);
	}

	if (customerGPUOne.length === 0 && !permissions.read) {
		return (
			<Container>
				<div
					style={{
						height: '50vh',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<p>You don't have Permission to View this information</p>
				</div>
			</Container>
		);
	}

	return (
		<Container>
			{viewMode === 'table' ? (
				<GPUTableView
					gpuData={filteredGPUData}
					onGPUClick={handleGPUClick}
					isLoading={isLoading}
					error={error}
					filters={filters}
					onFilterChange={handleFilterChange}
					onClearFilters={handleClearFilters}
					isStnUser={scopePermissions?.isStnUser}
					gpuoneLoginNode={gpuoneLoginNode}
					onDataRefresh={handleDataRefresh}
				/>
			) : (
				<GpuOneDetailView gpu={selectedGPU} onBack={handleBackToTable} />
			)}
		</Container>
	);
};

const GPUTableView = ({
	gpuData,
	onGPUClick,
	isLoading,
	error,
	filters,
	onFilterChange,
	onClearFilters,
	isStnUser,
	onDataRefresh,
	gpuoneLoginNode,
}) => {
	const [sortField, setSortField] = useState('last_updated');
	const [sortAsc, setSortAsc] = useState(false);

	const handleSort = (column, direction) => {
		setSortField(column?.id);
		setSortAsc(direction === 'asc');
		// For now, just refresh the data - sorting is handled by server
		// In the future, this could trigger a new API call with sort params
		onDataRefresh();
	};
	return (
		<Container>
			<Row>
				<Col>
					<PageHeader>
						<div>
							<h3 className="page-title">GPU One </h3>
						</div>
						<StatsContainer>
							<StatCard>
								<StatNumber>{gpuData.length}</StatNumber>
								<StatLabel>Total Servers</StatLabel>
							</StatCard>
							<StatCard>
								<StatNumber>
									{
										gpuData.filter(gpu => gpu.machine_status === 'Deployed')
											.length
									}
								</StatNumber>
								<StatLabel>Deployed</StatLabel>
							</StatCard>
							<StatCard>
								<StatNumber>
									{
										gpuData.filter(gpu => gpu.machine_status !== 'Deployed')
											.length
									}
								</StatNumber>
								<StatLabel>Available</StatLabel>
							</StatCard>
							<StatCard
								style={{ cursor: 'pointer' }}
								onClick={() => {
									if (gpuoneLoginNode) {
										window.open(gpuoneLoginNode, '_blank');
									} else {
										toast.info(
											'No Login Node found, Please add the Node in the Customer Settings',
										);
									}
								}}
							>
								<StatLabel
									style={{
										fontSize: '1rem',
										color: 'rgba(255, 255, 255, 0.9)',
										fontWeight: '500',
									}}
								>
									Login
									<br />
									Node
								</StatLabel>
							</StatCard>
						</StatsContainer>
					</PageHeader>
				</Col>
			</Row>

			{/* Filter Section */}
			<Row>
				<Col>
					<GpuOneFilterSection
						filters={filters}
						onFilterChange={onFilterChange}
						onClearFilters={onClearFilters}
					/>
				</Col>
			</Row>

			{/* Table Section */}
			<GpuOneTable
				data={gpuData}
				isLoading={isLoading}
				onRowClick={onGPUClick}
				isStnUser={isStnUser}
				onDataRefresh={onDataRefresh}
				sortServer={true}
				onSort={handleSort}
				defaultSortFieldId={sortField}
				defaultSortAsc={sortAsc}
			/>
		</Container>
	);
};

// Styled Components
const PageHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 30px;
	padding: 20px 0;
`;

const StatsContainer = styled.div`
	display: flex;
	gap: 20px;
`;

const StatCard = styled.div`
	background: linear-gradient(
		135deg,
		${colorAccent} 0%,
		${colorAccentHover} 100%
	);
	padding: 20px 25px;
	border-radius: 12px;
	text-align: center;
	min-width: 75px;
	box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
	transition: transform 0.3s ease;

	&:hover {
		transform: translateY(-5px);
	}
`;

const StatNumber = styled.div`
	font-size: 1rem;
	font-weight: 700;
	color: white;
	margin-bottom: 5px;
`;

const StatLabel = styled.div`
	font-size: 0.7rem;
	color: rgba(255, 255, 255, 0.9);
	font-weight: 500;
`;

export default GPUOnePage;
